import { useEffect, useState } from 'react';
import { api } from '../lib/api';

interface AuditEntry {
  id: string;
  actorId: string | null;
  targetUserId: string | null;
  action: string;
  payload: Record<string, unknown> | null;
  ipAddress: string | null;
  createdAt: string;
}

export default function AuditLog() {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const limit = 50;

  async function load() {
    setLoading(true);
    try {
      const data = await api.getAuditLog(limit, page * limit);
      setEntries(data.entries as AuditEntry[]);
    } catch {
      /* handled by api client redirect */
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [page]);

  const ACTION_COLORS: Record<string, string> = {
    adjust_tokens: 'bg-blue-100 text-blue-700',
    suspend_user: 'bg-red-100 text-red-700',
    activate_user: 'bg-green-100 text-green-700',
    delete_user: 'bg-red-200 text-red-800',
    upload_build: 'bg-purple-100 text-purple-700',
    activate_build: 'bg-indigo-100 text-indigo-700',
  };

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">Audit Log</h1>

      {loading ? (
        <p className="text-gray-500">Loading...</p>
      ) : entries.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <p className="text-lg">No audit entries yet</p>
          <p className="text-sm mt-1">Admin actions will appear here</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded border">
          <table className="w-full text-sm">
            <thead className="bg-gray-100 text-left">
              <tr>
                <th className="px-4 py-2">Time</th>
                <th className="px-4 py-2">Action</th>
                <th className="px-4 py-2">Actor</th>
                <th className="px-4 py-2">Target</th>
                <th className="px-4 py-2">Details</th>
                <th className="px-4 py-2">IP</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.id} className="border-t hover:bg-gray-50">
                  <td className="px-4 py-2 text-xs text-gray-500 whitespace-nowrap">
                    {new Date(e.createdAt).toLocaleString()}
                  </td>
                  <td className="px-4 py-2">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${ACTION_COLORS[e.action] ?? 'bg-gray-100 text-gray-700'}`}>
                      {e.action}
                    </span>
                  </td>
                  <td className="px-4 py-2 font-mono text-xs text-gray-500">
                    {e.actorId ? `${e.actorId.slice(0, 8)}…` : '—'}
                  </td>
                  <td className="px-4 py-2 font-mono text-xs text-gray-500">
                    {e.targetUserId ? `${e.targetUserId.slice(0, 8)}…` : '—'}
                  </td>
                  <td className="px-4 py-2 text-xs text-gray-600 max-w-xs truncate">
                    {e.payload ? JSON.stringify(e.payload) : '—'}
                  </td>
                  <td className="px-4 py-2 text-xs text-gray-400">
                    {e.ipAddress ?? '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      <div className="flex justify-between items-center mt-4">
        <button
          disabled={page === 0}
          onClick={() => setPage((p) => p - 1)}
          className="px-4 py-1.5 text-sm border rounded disabled:opacity-40 hover:bg-gray-50"
        >
          Previous
        </button>
        <span className="text-sm text-gray-600">Page {page + 1}</span>
        <button
          disabled={entries.length < limit}
          onClick={() => setPage((p) => p + 1)}
          className="px-4 py-1.5 text-sm border rounded disabled:opacity-40 hover:bg-gray-50"
        >
          Next
        </button>
      </div>
    </div>
  );
}
